/* eslint-disable */
import {
  Avatar,
  Box,
  Container,
  Drawer,
  Grid,
  IconButton,
  ListItem,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import Popover from "@mui/material/Popover";
import {
  alpha,
  styled
} from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import DOMPurify from "dompurify";
import {
  Fragment,
  useState,
  useEffect,
  useMemo,
  type Dispatch,
  type SetStateAction
} from "react";
import { useTranslation } from "react-i18next";
import {
  useLoaderData,
  useLocation,
  useNavigate
} from "react-router-dom";
import {
  CancelIcon,
  CancelIconWrapper,
  SearchInputBase,
  Search,
  SearchIcon,
  SearchIconWrapper,
  StylesresultsSearchMobile,
  ChatBotIcon
} from "./Header.styles";
import { TypeLevel } from "./TypeLevel.component";
import chatBotIcon from "../../assets/chatBotIcon.svg";
import logoFallback from "../../assets/digited_logo.png";
import logoMobileFallback from "../../assets/logoMobile.png";
import {
  ChatBot,
  Button,
  ButtonText,
  Icon,
  LanguageModal,
  Link,
  Logo,
  Notification
} from "../../components";
import { ImpersonationModal } from "../../components/ImpersonationModal";
import { NAVBAR_ITEMS, PERMISSIONS } from "../../consts";
import { useAISupportChat } from "../../context/AISupportChatContext";
import {
  useGetProfileImageQuery,
  useNotificationMutation,
  useNotificationQuery,
  useSearchPreviewQuery,
  type Notification as NotificationType
} from "../../queries";
import {
  useAuthStore,
  useLanguageStore,
  useNavbarStore,
  useSearchStore
} from "../../stores";
import type {
  ContentType,
  Level,
} from "../../types";
import { stringAvatar, getUserAvatar } from "../../utils/avatar";
import {getEnvironmentVariables, getLastPath, getPageTitle, checkCustomLogicFe} from "../../utils/general";
import { cleanAllCookies } from "../../utils/cookies";
import { ResultsStack } from "../SelectInput/SelectInput.styles";
import { t } from "i18next";
import {
  LucideBell,
  LucideBellDot,
  LucideChevronDown,
  LucideChevronLeft, LucideCornerDownRight,
  LucideBot, LucideGlobe, LucideLanguages, LucideLogOut,
  LucideSearch,
  LucideSparkles,
  LucideX
} from "lucide-react";

const { adminLink } = getEnvironmentVariables();

const DROPDOWN = {
  CHATBOT: "CHATBOT",
  MENU: "MENU",
  NOTIFICATIONS: "NOTIFICATIONS",
  SEARCH: "SEARCH"
} as const;
type Dropdown = keyof typeof DROPDOWN

export function Header({ authenticated=false, showTutorialUserMenu, isMultilanguage, languages, isPreLogin }: any) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // const userData = useAuthStore(state => state.userData);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  // const localUser = localStorage.getItem("authState");
  // const localUsername = localUser && JSON.parse(localUser || "")?.user.username;
  // const isImpersonation =  !localUsername?.includes(userData?.external_id);
  const isImpersonation = useAuthStore(state => state.isImpersonation);

  /* move to store/context */
  const [anchorElDropdown, setAnchorElDropdown] = useState<HTMLElement | null>(null);
  const [anchorElNotify, setAnchorElNotify] = useState<HTMLElement | null>(null);
  const [dropdown, setDropdown] = useState<Dropdown | null>(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false); // TODO: can this become local?
  const [searchValue, setSearchValue] = useState("");
  /* move to store/context */

  // Check if notifications should be hidden based on custom logic
  const { customLogicFe } = getEnvironmentVariables();
  const hideNotifications = checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hide_notifications" });
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [impersonationModalOpen, setImpersonationModalOpen] = useState(false);
  const { t } = useTranslation();
  const { openChat: openSupportChat } = useAISupportChat();

  useWrapper({
    hideDropdowns: () => { setDropdown(null) },
    setSearchValue,
    showSearchResults: dropdown === DROPDOWN.SEARCH
  });

  useEffect(() => {
    if (!!showTutorialUserMenu) {
      setDropdown(DROPDOWN.MENU)
    } else {
      setDropdown(null)
    }

  }, [showTutorialUserMenu])

  useEffect(() => {
    if(!isMobile && mobileSearchOpen) setMobileSearchOpen(false);
  }, [isMobile])

  return !isMobile ? (
    <HeaderAppBar  $mobileSearchOpen={ mobileSearchOpen } id="app-bar" role="banner" tabIndex={-1}>
      {dropdown && <CloseDropdowns closeDropdowns={ () => { setDropdown(null) } }/>}
      <ChatbotDrawer
        hideChatbot={ () => setDropdown(null) }
        showChatBot={ dropdown === DROPDOWN.CHATBOT }
      />
      {
        authenticated ?
          <NotificationsPopOver
            anchorElNotify={ anchorElNotify }
            hideNotifications={ () => setDropdown(null) }
            setAnchorElNotify={ setAnchorElNotify }
            showNotifications={ dropdown === DROPDOWN.NOTIFICATIONS }
          /> :
          null
      }

      <Container
        maxWidth="max"
        sx={ { "& .MuiToolbar-root": { paddingLeft: "18px" } } }
      >
        <Toolbar sx={ { p: "0 18px" } }>
          { /* left side elements */ }
          <Box sx={{
            width: "182px"
          }}>
            <LinkLogo authenticated={ authenticated  }/>
          </Box>
          { /* center elements */ }
          {
            // search
            authenticated  ? (
              <SearchBox
                setDropdown={ setDropdown }
                searchValue={ searchValue }
                setSearchValue={ setSearchValue }
                showSearchResults={ dropdown === DROPDOWN.SEARCH }
              />
            ) : null
          }
          { /* right side elements */ }
          {
            authenticated  ? (
              <Fragment>
                <Box
                  display="flex"
                  alignItems="center"
                  height={"2.5rem"}
                  gap={ 3 }
                  sx={ { justifyContent: "flex-end" } }
                >
                  { isImpersonation &&
                    <Tooltip title={t("end-impersonation-title")}>
                      <IconButton
                        aria-label={t("end-impersonation-title")}
                        sx={{
                          cursor: "pointer",
                          zIndex: "1050"
                        }}
                        onClick={ () => setImpersonationModalOpen(true) }
                      >
                        <Icon
                          icon={ "almost_there" }
                          size={"1.5rem"}
                          color={theme.customColors.headerIcon}
                        />
                      </IconButton>
                    </Tooltip>
                  }
                  { /* chatboticon */ }
                  {
                    (smartConfiguration?.chatbot) ? (
                      <Button  sx={ {
                          minWidth:"unset",
                          padding: "0",
                          position:"relative",
                          height: "100%",
                          zIndex: theme.zIndex.fab,
                          color: theme.customColors.headerIcon,
                          "&:hover": {
                              background: "transparent"
                          }
                          } }
                          onClick={ ()=> { setDropdown(DROPDOWN.CHATBOT) } }>
                        <LucideSparkles
                          aria-label={t("learning_coach")}
                          id="chatbot"
                          size={24}
                          color={theme.customColors.headerIcon}
                        />
                      </Button>
                    ) : null
                  }
                  { /* support ai icon */ }
                  <Button
                    aria-label={ t("support_ai") }
                    sx={ {
                      minWidth: "unset",
                      padding: "8px",
                      borderRadius: "8px",
                      position: "relative",
                      height: "100%",
                      zIndex: theme.zIndex.fab,
                      color: theme.customColors.textPrimaryCta,
                      backgroundColor: theme.customColors.backgroundPrimaryCta,
                      "&:hover": {
                        backgroundColor: theme.customColors.backgroundPrimaryCtaHover
                      }
                    } }
                    onClick={ () => { openSupportChat() } }>
                    <LucideBot
                      aria-label={ t("support_ai") }
                      size={ 24 }
                      color={ theme.customColors.textPrimaryCta }
                    />
                  </Button>
                  { /* notifications icon */ }
                  {!hideNotifications && (
                    <NotificationsIcon
                      showNotifications={ dropdown === DROPDOWN.NOTIFICATIONS }
                      setAnchorElNotify={ setAnchorElNotify }
                      setDropdown={ setDropdown }
                    />
                  )}
                  { /* avatar group */ }
                  <Grid
                    display={ "flex" }
                    justifyContent={ "space-between" }
                    gap={ 2 }
                    zIndex={ theme.zIndex.fab }
                  >
                    <ProfileIcon />
                    <MenuIcon
                      showDropdownMenu={ dropdown === DROPDOWN.MENU }
                      setAnchorElDropdown={ setAnchorElDropdown }
                      setDropdown={ setDropdown }
                    />
                  </Grid>
                </Box>
                { /* dropdown menu */ }
                <DropdownMenu
                  setLanguageModalOpen={ setLanguageModalOpen }
                  isMultilanguage={ Boolean(isMultilanguage) }
                  anchorElDropdown={ anchorElDropdown }
                  hideMenu={ () => setDropdown(null) }
                  showChatBot={ () => setDropdown(DROPDOWN.CHATBOT) }
                  showDropdownMenu={ dropdown === DROPDOWN.MENU }
                />
              </Fragment>
            ) : null
          }
          {
            !authenticated && (isPreLogin || isMultilanguage) ? (
              <Stack
                alignItems="center"
                direction="row"
                flexGrow={ 1 }
                gap={ 2 }
                justifyContent="flex-end"
                zIndex={ theme.zIndex.fab }
              >
                { isPreLogin ? <SupportAiButton /> : null }
                { isMultilanguage ? (
                  <LanguageSelector setOpenLanguageModal={ setLanguageModalOpen } />
                ) : null }
              </Stack>
            ) : null
          }
        </Toolbar>
      </Container>
      {languageModalOpen && (
          <LanguageModal
          close={ () => {setLanguageModalOpen(false)} }
          languages={ languages ?? [] }
          open={ languageModalOpen }
          />
        )
      }
      <ImpersonationModal
        close={ () => setImpersonationModalOpen(false) }
        open={ impersonationModalOpen }
      />
    </HeaderAppBar>
  ) : (
    <HeaderAppBar $mobileSearchOpen={ mobileSearchOpen } role="none">
      <CloseDropdowns closeDropdowns={ () => { setDropdown(null) } }/>
      <ChatbotDrawer
        hideChatbot={ () => setDropdown(null) }
        showChatBot={ dropdown === DROPDOWN.CHATBOT }
      />
      {
        mobileSearchOpen ?
          <MobileSearchDrawer
            mobileSearchOpen={ mobileSearchOpen }
            searchValue={ searchValue }
            setMobileSearchOpen={ setMobileSearchOpen }
            setSearchValue={ setSearchValue }
          /> : null

      }

      {
        authenticated  ?

          <NotificationsDrawer
            hideNotification={ () => setDropdown(null) }
            showNotifications={ dropdown === DROPDOWN.NOTIFICATIONS }
          /> : null
      }

      <Container maxWidth="max" sx={ { "& .MuiToolbar-root": { paddingLeft: isMobile? "0" : "18px" } } }>
        <Toolbar sx={ { minHeight: "auto", p: "0" } }>
          { /* left side elements */ }
          <LinkLogo authenticated={ authenticated }/>
          { /* right side elements */ }
          {
            authenticated  ? (
              <Fragment>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={ 3 }
                  height={"2.5rem"}
                  sx={ { justifyContent: "flex-end", width: "100%" } }
                >
                  { isImpersonation &&
                    <Tooltip title={t("end-impersonation-title")}>
                      <IconButton
                        sx={{
                          cursor: "pointer",
                          zIndex: "1050"
                        }}
                        onClick={ () => setImpersonationModalOpen(true) }
                      >
                        <Icon
                          icon={ "almost_there" }
                          size={"1.5rem"}
                          color={theme.customColors.headerIcon}
                        />
                      </IconButton>
                    </Tooltip>
                  }
                  { /* search icon (mobile) */ }
                  <Button
                    aria-label={t("search_contents")}
                    className={ "search-opener" }
                    disableRipple
                    onClick={ () => { setMobileSearchOpen(true) } }
                    sx={ {
                      minWidth:"unset",
                      padding: "0",
                      zIndex: theme.zIndex.fab,
                      height: "100%",
                      "&:focus-visible": {
                        background: "transparent",
                        boxShadow: "none"
                      }
                    } }
                  >
                    <LucideSearch
                      size={ 24 }
                      color={theme.customColors.textPrimary}
                    />
                  </Button>
                  { /* support ai icon */ }
                  <Button
                    aria-label={ t("support_ai") }
                    sx={ {
                      minWidth: "unset",
                      padding: "8px",
                      borderRadius: "8px",
                      position: "relative",
                      height: "100%",
                      zIndex: theme.zIndex.fab,
                      color: theme.customColors.textPrimaryCta,
                      backgroundColor: theme.customColors.backgroundPrimaryCta,
                      "&:hover": {
                        backgroundColor: theme.customColors.backgroundPrimaryCtaHover
                      }
                    } }
                    onClick={ () => { openSupportChat() } }>
                    <LucideBot
                      aria-label={ t("support_ai") }
                      size={ 24 }
                      color={ theme.customColors.textPrimaryCta }
                    />
                  </Button>
                  { /* notifications icon */ }
                  {!hideNotifications && (
                    <NotificationsIcon
                      showNotifications={ dropdown === DROPDOWN.NOTIFICATIONS }
                      setDropdown={ setDropdown }
                    />
                  )}
                  { /* avatar group */ }
                  <Grid
                    display={ "flex" }
                    justifyContent={ "space-between" }
                    gap={ 1 }
                    zIndex={ theme.zIndex.fab }
                  >
                    <ProfileIcon />
                    <MenuIcon
                      showDropdownMenu={ dropdown === DROPDOWN.MENU }
                      setAnchorElDropdown={ setAnchorElDropdown }
                      setDropdown={ setDropdown }
                    />
                  </Grid>
                </Box>
                { /* dropdown menu */ }
                <DropdownMenu
                  setLanguageModalOpen={ setLanguageModalOpen }
                  isMultilanguage={ Boolean(isMultilanguage) }
                  anchorElDropdown={ anchorElDropdown }
                  hideMenu={ () => setDropdown(null) }
                  showChatBot={ () => setDropdown(DROPDOWN.CHATBOT) }
                  showDropdownMenu={ dropdown === DROPDOWN.MENU }
                  showTutorialUserMenu={showTutorialUserMenu}
                />
              </Fragment>
            ) : null
          }
          {
            !authenticated && (isPreLogin || isMultilanguage) ? (
              <Stack
                alignItems="center"
                direction="row"
                flexGrow={ 1 }
                gap={ 2 }
                justifyContent="flex-end"
                zIndex={ theme.zIndex.fab }
              >
                { isPreLogin ? <SupportAiButton /> : null }
                { isMultilanguage ? (
                  <LanguageSelector
                    setOpenLanguageModal={ setLanguageModalOpen } />
                ) : null }
              </Stack>
            ) : null
          }
        </Toolbar>
      </Container>
      <LanguageModal
        close={ () => setLanguageModalOpen(false) }
        languages={ languages ?? [] }
        open={ languageModalOpen }
      />
      <ImpersonationModal
        close={ () => setImpersonationModalOpen(false) }
        open={ impersonationModalOpen }
      />
    </HeaderAppBar>
  );
}

// TODO: farmi spiegare da Robi wtf, needed on mobile? setShowSearchResults?
function useWrapper({
  hideDropdowns,
  setSearchValue,
  showSearchResults
} : {
  hideDropdowns: () => void
  setSearchValue: Dispatch<SetStateAction<string>>
  showSearchResults: boolean
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const wrapper = document.querySelector("#root .mask");
  wrapper?.addEventListener("click", () => {
    if (showSearchResults) {
      wrapper?.classList.remove("has-mask");
      hideDropdowns();
    }
  });

  useEffect(() => {
    if (showSearchResults) {
      if(isMobile){
        wrapper?.classList.remove("has-mask");
      }else{
        wrapper?.classList.add("has-mask");
      }
    } else {
      setSearchValue("");
      wrapper?.classList.remove("has-mask");
    }
  }, [wrapper, showSearchResults, setSearchValue, isMobile]);
}

function NotificationsDrawer({
  hideNotification,
  showNotifications
} : {
  hideNotification: () => void
  showNotifications: boolean
}) {
  const { data: userNotifications } = useNotificationQuery({});
  const { mutate: postNotification } = useNotificationMutation();

  return (
    <Drawer
      anchor="right"
      hideBackdrop
      variant={ "temporary" }
      open={ showNotifications }
      onClose={ hideNotification }
      ModalProps={ { keepMounted: true } } // Better open performance on mobile.
      sx={ {
        "& .MuiDrawer-paper": {
          bottom: "0",
          boxSizing: "border-box",
          height: "calc(100% - 124px)",
          //top: "90px",
          top: "64px",
          width: "100%"
        }
      } }
    >
      <Notification
        contents={ userNotifications }
        onClick={ clickNotification }
        onClickLink={ () => hideNotification() }
        onMarkAllAsRead={ markAllAsRead }
      />
    </Drawer>
  );

  function clickNotification(notification: NotificationType) {
    if (notification.isRead !== "Y") {
      postNotification({
        messagesId: notification.eventSentId,
        status: "Y"
      });
    }
  }

  function markAllAsRead() {
    const unreadIds = userNotifications
      ?.filter(n => n.isRead !== "Y")
      .map(n => n.eventSentId) ?? [];
    if (unreadIds.length === 0) return;
    postNotification({ messagesId: unreadIds, status: "Y" });
  }
}

const StyledPopover = styled(Popover)(({ theme }) => ({
  zIndex: theme.zIndex.tooltip,

  ".MuiPopover-paper": {
    border: `1px solid ${theme.customColors.border} !important`,
    borderRadius: `${theme.spacing(1)} !important`
  }
}));

function NotificationsPopOver({
  anchorElNotify,
  hideNotifications,
  setAnchorElNotify,
  showNotifications
} : {
  anchorElNotify: HTMLElement | null
  hideNotifications: () => void
  setAnchorElNotify: Dispatch<SetStateAction<HTMLElement | null>>
  showNotifications: boolean
}) {
  const { data: userNotifications } = useNotificationQuery({});
  const { mutate: postNotification } = useNotificationMutation();

  return (
    <StyledPopover
      anchorEl={ anchorElNotify }
      anchorOrigin={ {
        horizontal: 20,
        vertical: "bottom"
      } }
      classes={ { paper:"scrollable" } }
      className={ "popover-notification" }
      id={ showNotifications ? "simple-popover" : undefined }
      onClose={ () => {
        hideNotifications();
        setAnchorElNotify(null);
      } }
      open={ Boolean(showNotifications) }
      transformOrigin={ {
        horizontal: "right",
        vertical: -20
      } }
    >
      <Notification
        contents={ userNotifications }
        onClick={ clickNotification }
        onClickLink={ () =>  hideNotifications() }
        onMarkAllAsRead={ markAllAsRead }
      />
    </StyledPopover>
  );

  function clickNotification(notification: NotificationType) {
    if (notification.isRead !== "Y") {
      postNotification({
        messagesId: notification.eventSentId,
        status: "Y"
      });
    }
  }

  function markAllAsRead() {
    const unreadIds = userNotifications
      ?.filter(n => n.isRead !== "Y")
      .map(n => n.eventSentId) ?? [];
    if (unreadIds.length === 0) return;
    postNotification({ messagesId: unreadIds, status: "Y" });
  }
}

function MobileSearchDrawer({
  mobileSearchOpen,
  searchValue,
  setMobileSearchOpen,
  setSearchValue
} : {
  mobileSearchOpen: boolean
  searchValue: string
  setMobileSearchOpen: Dispatch<SetStateAction<boolean>>
  setSearchValue: Dispatch<SetStateAction<string>>
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { pathname, state: routerState } = useLocation();
  const navigate = useNavigate();

  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  const latestSearch = useSearchStore((state) => state.latestSearch);
  const pushSearch = useSearchStore((state) => state.pushSearch);

  const [mobileShowResults, setMobileShowResults] = useState(false);

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  const { data: results } = useSearchPreviewQuery({ freeText: encodeURIComponent(debouncedSearchValue) });

  return (
    <Drawer
      hideBackdrop={ true }
      className="drawer-search-mobile"
      anchor="right"
      variant={ mobileShowResults ? "permanent" : "temporary" }
      open={ mobileSearchOpen }
      onClose={ () => setMobileSearchOpen(false) }
      // ModalProps={ {
      // // TODO: CAREFUL with keepMounted, the drawer queries are supposed to "refetchOnMount"
      //   keepMounted: true // Better open performance on mobile.
      // } }
      sx={ {
        ".MuiDrawer-paper": {
          boxSizing: "border-box",
          maxHeight: mobileShowResults? "61px" : "100%",
          width: "100%",
          zIndex: theme.zIndex.appBar
        }
      } }
    >
      <Box
        sx={ {
          alignItems: "center",
          borderBottom: `1px solid ${theme.customColors.border}`,
          display: "flex",
          gap: "24px",
          justifyContent: "space-between",
          padding: "10px 20px"
        } }
      >
        <Link aria-label={t("back")} href={ routerState?.previous ?? "/" } style={{height: "24px"}}>
          <LucideChevronLeft
            color={ theme.customColors.headerIcon }
            size={24}
            onClick={ () => {
              setMobileSearchOpen(false);
              setMobileShowResults(false);
            } }
          />
        </Link>
        <Search
          sx={ {
            maxWidth: "unset",
            width: "100%",
            [theme.breakpoints.down("sm")]: {
              display: "flex",
              padding: "8px 16px"
            }
          } }
        >
          <SearchIconWrapper onClick={ () => {
            getSearchResults();
            setMobileShowResults(true);
            setMobileSearchOpen(true);
          } }>
            <LucideSearch
              aria-hidden="true"
              color={ theme.customColors.backgroundPrimaryCta }
              size={24}
            />
          </SearchIconWrapper>
          <SearchInputBase
            value={ searchValue }
            onChange={ (changeEvent) => setSearchValue(changeEvent.target.value) }
            inputProps={ { "aria-label": t("search_contents") } }
            onKeyUp={ (keyboardEvent) => {
              if (keyboardEvent.code === "Enter" && searchValue.length > 0) {
                pushSearch({
                  id: null,
                  learningObjectType: null,
                  learningObjectTypology: null,
                  title: (keyboardEvent.target as HTMLInputElement).value
                });
                navigate(
                  `/search/${(keyboardEvent.target as HTMLInputElement).value}`,
                  { state: { previous: pathname } }
                );
                //setNavbarItem("");
                setMobileSearchOpen(false);
                setMobileShowResults(true);
              }
            } }
            placeholder={ `${t("what_to_learn")}` }
          />
          <CancelIconWrapper
             tabIndex={searchValue.length > 0 ? 0 : -1 }
             aria-label={ t("empty_field") }
             aria-hidden={searchValue.length === 0 ? "true" : undefined }
            onClick={ () => setSearchValue("") }
            onKeyDown={ (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setSearchValue("");
              }
            } }>
            {
              searchValue ? (
                <LucideX size={24} color={ theme.customColors.backgroundPrimaryCta } />
              ) : null
            }
          </CancelIconWrapper>
        </Search>
      </Box>
      {
        !mobileShowResults ? (
          <StylesresultsSearchMobile>
            { /* has no search value and has previous search */ }
            {
              (searchValue.length === 0 && latestSearch?.length === 0) ? (
                <Typography
                  color={ theme.palette.text?.secondary }
                  tabIndex={searchValue.length == 0 && latestSearch?.length == 0 ? 0 : -1}
                  fontSize="0.75rem"
                  fontWeight={ "700" }
                  lineHeight={ 1 }
                  aria-hidden="true"
                >
                  { t("no_recent_search") }
                </Typography>
              ) : null
            }
            { /* has no search value and has no previous search */ }
            {
              searchValue.length === 0 && latestSearch?.length > 0 ? (
                <Fragment>
                  <Typography
                    tabIndex={!mobileShowResults ? -1 : 0}
                    color={ theme.palette.text?.secondary }
                    fontSize="0.75rem"
                    fontWeight={ "700" }
                    lineHeight={ 1 }
                  >
                    { t("latest_research") }
                  </Typography>
                  {
                    latestSearch?.map(({
                      id,
                      learningObjectType,
                      learningObjectTypology,
                      title
                    }, idx) => (
                      <Link
                        aria-hidden={!mobileShowResults ? undefined : true}
                        key={ `search-result--${idx}` }
                        href={
                          Number(id) > 0
                            ? `/esplora/dettaglio/${id}/${learningObjectTypology}`
                            : `/search/${title}`
                        }
                        state={ { previous: pathname } }
                        onClick={ () => {
                          setMobileSearchOpen(false);
                          setMobileShowResults(true);
                        } }
                      >
                        <Box
                          display={ "flex" }
                          flexDirection={ "column" }
                          gap={ 1 }
                          onClick={ () => {
                            setSearchValue(title);
                            setNavbarItem(
                              Number(id) > 0
                                ? NAVBAR_ITEMS.EXPLORE
                                : NAVBAR_ITEMS.NONE
                            );
                          } }
                        >
                          {
                            (learningObjectType !== null && learningObjectTypology !== null) ? (
                              <TypeLevel
                                type={ learningObjectType as ContentType }
                                learningObjectTypology={ learningObjectTypology as Level }
                              />
                            ) : null
                          }
                          <Typography
                            color="black"
                            lineHeight={ 1 }
                            fontWeight={ 700 }
                            sx={ {
                              [theme.breakpoints.down("sm")]: {
                                color: theme.customColors.textPrimary,
                                textTransform: "capitalize"
                              }
                            } }
                          >
                            { title }
                          </Typography>
                        </Box>
                      </Link>
                    ))
                  }
                </Fragment>
              ) : null
            }
            { /* has search value and has no results */ }
            {
              searchValue.length > 0 && results?.length === 0 ? (
                <Typography
                  aria-hidden={searchValue.length == 0 ||  results?.length > 0 ? "true":undefined}
                  color={ theme.palette.text?.secondary }
                  fontSize="0.75rem"
                  fontWeight={ "700" }
                  lineHeight={ 1 }
                >
                  { t("no_results") + `"${searchValue}"` }
                </Typography>
              ) : null
            }
            { /* has search value (implicit) and has results */ }
            {
              results?.map(({
                id,
                learningObjectType,
                title,
                type
              }, idx) => (
                <Link
                  key={ `search-result--${idx}` }
                  href={ `/esplora/dettaglio/${id}/${type}` }
                  state={ {
                    previous: pathname
                  } }
                  onClick={ ()=>{
                    setMobileShowResults(true);
                    setMobileSearchOpen(false);
                  } }>
                  <Box
                    display={ "flex" }
                    flexDirection={ "column" }
                    gap={ 1 }
                    onClick={ () => {
                      setSearchValue(title);
                      pushSearch({
                        id,
                        learningObjectType: learningObjectType,
                        learningObjectTypology: type,
                        title: title
                      });
                      setNavbarItem(NAVBAR_ITEMS.EXPLORE);
                    } }
                    sx={ {
                      cursor: "pointer",

                      ":hover": {
                        boxShadow:
                        /* eslint-disable max-len */
                                      `0 0 20px ${theme.palette.primary?.main && alpha(theme.palette.primary?.main, 0.15)},
                                      inset 0 0 40px ${theme.palette.primary?.main && alpha(theme.palette.primary?.main, 0.15)}`
                      /* eslint-enable max-len */
                      }
                    } }
                  >
                    <TypeLevel
                      type={ learningObjectType }
                      learningObjectTypology={ type }
                    />
                    <HighlightedResult
                      searchValue={ searchValue }
                      title={ title }
                    />
                  </Box>
                </Link>
              ))
            }
          </StylesresultsSearchMobile>
        ) : null
      }
    </Drawer>
  );

  function getSearchResults () {
    if (searchValue.length > 0){
      pushSearch({
        id: null,
        learningObjectType: null,
        learningObjectTypology: null,
        title: searchValue
      });
      navigate(
        `/search/${encodeURIComponent(searchValue)}`,
        {
          state: {
            previous: pathname
          }
        }
      );
    }
  }
}

function HighlightedResult({
  searchValue,
  title
}: {
  searchValue: string
  title: string
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const result = boldString(title, searchValue);

  return result ? (
    <Typography
      color={ theme.customColors.textDisabled }
      lineHeight={ 1 }
      fontWeight={ 700 }
      textTransform={ "capitalize" }
      sx={ {
        "b":{
          color: isMobile
            ? theme.customColors.textPrimary
            : theme.palette.common?.black
        }
      } }
      dangerouslySetInnerHTML={ {
        __html: DOMPurify.sanitize(result)
      } }
    >
    </Typography>
  ) : (
    <Typography
      color={ theme.customColors.textDisabled }
      lineHeight={ 1 }
      fontWeight={ 700 }
    >
      { title }
    </Typography>
  );
  function boldString(str:string, substr:string) {
    return str.toLocaleLowerCase()?.replace(substr, `<b>${substr}</b>`);
  }


}

function ChatbotDrawer({
  showChatBot,
  hideChatbot
} : {
  showChatBot: boolean
  hideChatbot: () => void
}) {
  const theme = useTheme();

  return (
    <Drawer
      anchor="bottom"
      open={ showChatBot }
      className={ "chatBot-drawer" }
      onClose={ hideChatbot }
      sx={ {
        "& .MuiDrawer-paper": {
          background:"transparent",
          borderRadius:"4px",
          boxShadow:"none",

          bottom:"50px",
          left:"auto",
          right:"50px",
          top:"auto",

          height:"calc(100% - 70px)",
          maxHeight:"575px",
          overflowY:"visible",
          width:"320px",
          zIndex:1400,

          [`${theme.breakpoints.down("sm")}`]:{
            bottom:"77.83px",
            left:0,
            right:0,

            margin:"0 auto"
          }
        }
      } }
    >
      <ChatBot onClose={ hideChatbot } />
    </Drawer>
  );
}

function SearchBox({
  searchValue,
  setDropdown,
  setSearchValue,
  showSearchResults
} : {
  setDropdown: Dispatch<SetStateAction<Dropdown | null>>
  searchValue: string
  setSearchValue: Dispatch<SetStateAction<string>>
  showSearchResults: boolean
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  const latestSearch = useSearchStore((state) => state.latestSearch);
  const pushSearch = useSearchStore((state) => state.pushSearch);

  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 300);

    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  const { data: results } = useSearchPreviewQuery({ freeText: encodeURIComponent(debouncedSearchValue) });
  const [isTab, setIsTab] = useState(false);
  const [focusIn, setFocusIn] = useState("");

  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const focusedElement = e.target as HTMLElement;
      setFocusIn(focusedElement?.id);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        setIsTab(true);
      }
    };

    const handleMouseDown = () => {
      setIsTab(false);
    };
    document.addEventListener("focusin", handleFocusIn);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("mousedown", handleMouseDown);
    return () => {
      document.removeEventListener("focusin", handleFocusIn);
    };
  }, []);

  return (
    <Box
    onBlur={(e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        setDropdown(null);
      }
    }}
      display="flex"
      flexGrow={ 1 }
      justifyContent="center"
    >
      <Search>
        <SearchIconWrapper
          onClick={ () => {
            getSearchResults();
            setDropdown(null);
            setNavbarItem(NAVBAR_ITEMS.NONE);
          } }
        >
          <LucideSearch size={24} aria-hidden={"true"} color={ theme.customColors.backgroundPrimaryCta }/>
        </SearchIconWrapper>
        <SearchInputBase
        id="search-input-header"
         sx={{
              "& input:focus": {
                 outline: "none",
                 boxShadow: "none"
                },
                  ...(focusIn === "search-input-header" && isTab && {
              "& input:focus-visible": {
                 outline: `4px solid ${theme.customColors.systemSecondary01}`,
                 outlineOffset: "4px"
                }
              })
             }}
          value={ searchValue }
          onChange={ (changeEvent) => {
            setDropdown(DROPDOWN.SEARCH);
            setSearchValue(changeEvent.target.value);
          } }
          inputProps={ { "aria-label": t("search_contents") } }
          onClick={ () => { setDropdown(DROPDOWN.SEARCH) } }
          onKeyUp={ (keyboardEvent) => {
            if(keyboardEvent.code === "Enter" && searchValue.length > 0) {
              setDropdown(null);
              pushSearch({
                id: null,
                learningObjectType: null,
                learningObjectTypology: null,
                title: (keyboardEvent.target as HTMLInputElement).value
              });
              navigate(
                `/search/${encodeURIComponent((keyboardEvent.target as HTMLInputElement).value)}`,
                { state: { previous: pathname } }
              );
              setNavbarItem(NAVBAR_ITEMS.NONE);
            }
          } }
          placeholder={ `${t("what_to_learn")}` }
        />
        <CancelIconWrapper
         sx={{
           "&:focus": {
          outline: "none"
          },
           '&:focus-visible': {
          outline: `4px solid ${theme.customColors.systemSecondary01}`,
          outlineOffset: "4px"
          }
          }}
          tabIndex={searchValue.length > 0 ? 0 : -1 }
          aria-label={ t("empty_field") }
          aria-hidden={searchValue.length === 0 ? "true" : undefined }
          onClick={ () => {
            setDropdown(null);
            setSearchValue("");
          } }
          onKeyUp={ (e) => {
            if (e.key === "Enter" || e.key === " ") {
              setDropdown(null);
              setSearchValue("");
            }
          } }
          >
          {
            searchValue ? (
              <LucideX size={24} color={ theme.customColors.backgroundPrimaryCta } />
            ) : null
          }
        </CancelIconWrapper>
        {
          (showSearchResults || searchValue.length > 0) && (
            <ResultsStack
              aria-hidden={  searchValue.length == 0 && !showSearchResults? "true": undefined }
              className="scrollable"
              $isVisible={ showSearchResults && (searchValue.length > 0 || latestSearch?.length > 0) }
              padding={ theme.spacing(2.5, 2) }
              gap={ 2 }
            >
              {
                (searchValue.length == 0 && latestSearch?.length == 0) ? (
                  <Typography
                    tabIndex={searchValue.length && latestSearch?.length > 0 ? 0 : -1}
                    color={ theme.palette.text?.secondary }
                    fontSize="0.75rem"
                    fontWeight={ "700" }
                    lineHeight={ 1 }
                  >
                    { t("no_recent_search") }
                  </Typography>
                ) : null
              }
              {
                searchValue.length == 0 && latestSearch?.length > 0 ? (
                  <Typography
                    tabIndex={!showSearchResults ? -1 : 0}
                    color={ theme.palette.text?.secondary }
                    fontSize="0.75rem"
                    fontWeight={ "700" }
                    lineHeight={ 1 }
                  >
                    { t("latest_research") }
                  </Typography>
                ) : null
              }
              {
                searchValue.length == 0 ? (
                  latestSearch?.map(({
                                       id,
                                       learningObjectType,
                                       learningObjectTypology,
                                       title
                                     }, idx) => (
                    <ListItem  aria-hidden={!showSearchResults ? "true":undefined} key={ `search-result--${idx}` } >
                      <Link
                        sx={{
                         "&:focus": {
                          outline: "none"
                         },
                         '&:focus-visible': {
                           outline: `4px solid ${theme.customColors.backgroundPrimaryCta}`,
                           outlineOffset: "4px"
                         }
                         }}
                        tabIndex={showSearchResults ? 0: -1}
                        href={
                          Number(id) > 0 ?
                            `/esplora/dettaglio/${id}/${learningObjectTypology}` : `/search/${encodeURIComponent(title)}`
                        }
                        state={ {
                          previous: pathname
                        } }
                      >
                        <Box
                          role="button"
                          display={ "flex" }
                          flexDirection={ "column" }
                          gap={ 1 }
                          onClick={ () => {
                            setDropdown(null);
                            setSearchValue(title);
                            pushSearch({
                              id,
                              learningObjectType: learningObjectType,
                              learningObjectTypology,
                              title: title
                            });
                            setNavbarItem(NAVBAR_ITEMS.EXPLORE);
                          } }
                        >
                          {
                            (learningObjectType !== null && learningObjectTypology !== null) &&
                            <TypeLevel
                              type={ learningObjectType as ContentType }
                              learningObjectTypology={ learningObjectTypology as Level }
                            />
                          }
                          <Typography
                            color="black"
                            lineHeight={ 1 }
                            fontWeight={ 700 }
                          >
                            { title }
                          </Typography>
                        </Box>
                      </Link>
                    </ListItem>
                  ))
                ) : null
              }
              {
                (searchValue.length > 0 && results?.length == 0) ? (
                  <Typography
                    sx={{
                        "&:focus": {
                          outline: "none"
                         },
                        '&:focus-visible': {
                          outline: `2px solid ${theme.customColors.backgroundPrimaryCta}`,
                          outlineOffset: "2px"
                         }
                         }}
                    aria-hidden={searchValue.length == 0 ||  results?.length > 0 ? "true":undefined}
                    color={ theme.palette.text?.secondary }
                    fontSize="0.75rem"
                    fontWeight={ "700" }
                    lineHeight={ 1 }
                  >
                    { t("no_results") + `"${searchValue}"` }
                  </Typography>
                ) : null
              }
              { results && results?.length > 0 ?
                <ul className="block">{
                  results?.map(({
                                  id,
                                  learningObjectType,
                                  title,
                                  type
                                }, idx) => (
                    <ListItem aria-hidden={results?.length == 0 ? "true": undefined} key={ `search-result--${idx}` }>

                      <Link sx={{
                         "&:focus": {
                          outline: "none"
                         },
                         '&:focus-visible': {
                           outline: `4px solid ${theme.customColors.systemSecondary01}`,
                           outlineOffset: "4px"
                         }
                         }}
                        tabIndex={results?.length > 0 ? 0: -1}
                        href={ `/esplora/dettaglio/${id}/${type}` }
                        state={ {
                          previous: pathname
                        } }
                      >
                        <Box
                          display={ "flex" }
                          flexDirection={ "column" }
                          gap={ 1 }
                          onClick={ () => {
                            setDropdown(null);
                            setSearchValue(title);
                            pushSearch({
                              id,
                              learningObjectType: learningObjectType,
                              learningObjectTypology: type,
                              title: title
                            });
                            setNavbarItem(NAVBAR_ITEMS.EXPLORE);
                          } }
                          sx={ {
                            cursor: "pointer",
                          } }
                        >
                          <TypeLevel
                            type={ learningObjectType }
                            learningObjectTypology={ type }
                          />
                          <HighlightedResult
                            searchValue={ searchValue }
                            title={ title }
                          />
                        </Box>
                      </Link>
                    </ListItem>
                  ))
                } </ul>
                : null }
            </ResultsStack>
          )
        }
      </Search>
    </Box>
  );

  function getSearchResults () {
    if (searchValue.length > 0){
      pushSearch({
        id: null,
        learningObjectType: null,
        learningObjectTypology: null,
        title: searchValue
      });
      navigate(
        `/search/${searchValue}`,
        {
          state: {
            previous: pathname
          }
        }
      );
    }
  }
}

function NotificationsIcon({
  showNotifications,
  setAnchorElNotify, // not need for mobile
  setDropdown
}: {
  showNotifications: boolean
  setAnchorElNotify?: Dispatch<SetStateAction<HTMLElement | null>>
  setDropdown: Dispatch<SetStateAction<Dropdown | null>>
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { data: userNotifications } = useNotificationQuery({});

  const unreadNotifications = userNotifications?.filter((notification) => notification.isRead === "N") ?? [];

  return (
    <Button
      title= { t("notify_text") }
      disableRipple
      color={ "inherit" }
      aria-describedby={ showNotifications ? "simple-popover" : undefined }
      onClick={ (clickEvent) => {
        showNotifications && setDropdown(null);
        showNotifications || setDropdown(DROPDOWN.NOTIFICATIONS);
        setAnchorElNotify && setAnchorElNotify(clickEvent.currentTarget);
      } }
      sx={ {
        minWidth:"unset",
        paddingY: "0",
        paddingX: "5px",
        position:"relative",
        height: "100%",
        zIndex: theme.zIndex.fab,
        color: theme.customColors.headerIcon,
        "&:hover": {
          background: "transparent"
        }
      } }
    >
      <>
        <LucideBell
          size={24}
        />

        {unreadNotifications.length ? (
          <Box sx={{
            display: "flex",
            width: "20px",
            height: "20px",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            top: 0,
            right: 0,
            borderRadius: "999px",
            backgroundColor: theme.customColors.backgroundPrimaryCta,
          }}>
            <Typography style={{fontSize: "10px"}} color={theme.customColors.textPrimaryCta}>{unreadNotifications.length}</Typography>
          </Box>
        ) : <div/>}
      </>
    </Button>
  );
}

function ProfileIcon() {
  const theme = useTheme();
  const userData = useAuthStore(state => state.userData);

  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);

  const { data: profileImage  } = useGetProfileImageQuery({});

  const userAvatar = getUserAvatar(profileImage, userData);

  return (
    <Link
      sx={{
        "&:focus": {
            outline: "none"
          },
        '&:focus-visible': {
            outline: `2px solid ${theme.customColors.headerIcon}`,
            outlineOffset: "2px" }
          }}
      aria-label={ `${t("go_profile")} - ${userData?.name} ${userData?.surname}` }
      href="/profile"
      onClick={ ()=> { setNavbarItem(NAVBAR_ITEMS.NONE) } }
      zIndex={ theme.zIndex.fab }
    >
      <Avatar
        aria-hidden={true}
        { ...stringAvatar(userAvatar.initials) }
        src={ userAvatar.src }
        sx={ {
          width:"2.5rem",
          height:"2.5rem",
          color: theme.customColors?.backgroundPrimaryLighterAlternative
        } }
      />
    </Link>
  );

}

function MenuIcon({
  showDropdownMenu,
  setAnchorElDropdown,
  setDropdown
} : {
  showDropdownMenu: boolean,
  setAnchorElDropdown: Dispatch<SetStateAction<HTMLElement | null>>
  setDropdown: Dispatch<SetStateAction<Dropdown | null>>
}) {
  const theme = useTheme();

  return (
    <Tooltip title={t("open_settings")}>
      <IconButton
        aria-expanded={showDropdownMenu}
        onClick={ (clickEvent) => {
          setAnchorElDropdown(clickEvent.currentTarget);
          showDropdownMenu && setDropdown(null);
          showDropdownMenu || setDropdown(DROPDOWN.MENU);
        } }
        sx={ {
          color: theme.customColors.systemPrimary01,
          p: 0,
          transform: showDropdownMenu ? " rotate(180deg)" : " rotate(0deg)",
          transition: "transform .4s linear",
          "&.Mui-focusVisible": {
            outline: `2px solid ${theme.customColors.headerIcon}`,
            outlineOffset: "2px",
            borderRadius: "2px"
          },
          ":hover": {
            background: "transparent"
          }

        } }
      >
        <LucideChevronDown
          aria-hidden="true"
          color={ theme.customColors.headerIcon }
          size={24}
        />
      </IconButton>
    </Tooltip>
  );
}

const StyledMenu = styled(Menu)(({ theme }) => ({
  zIndex: theme.zIndex.tooltip
}));

function DropdownMenu({
  anchorElDropdown,
  hideMenu,
  isMultilanguage,
  setLanguageModalOpen,
  showChatBot,
  showDropdownMenu,
  showTutorialUserMenu
} : {
  anchorElDropdown: HTMLElement | null
  hideMenu: () => void
  isMultilanguage:boolean
  setLanguageModalOpen:Dispatch<SetStateAction<boolean>>
  showChatBot: () => void
  showDropdownMenu: boolean
  showTutorialUserMenu?: boolean
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const authenticationMode = useAuthStore(state => state.mode);
  const language = useLanguageStore(state => state.language);
  const session = useAuthStore(state => state.session);
  const sessionToken = useAuthStore(state => state.sessionToken);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);
  const userData = useAuthStore(state => state.userData);
  const userInfo = useAuthStore(state => state.userInfo);
  const postInitiativeId = useAuthStore(state => state.postInitiativeId);
  const signOut = useAuthStore(state => state.signOut);
  // const localUser = localStorage.getItem("authState");
  // const localUsername = localUser && JSON.parse(localUser || "")?.user.username;
  // const isImpersonation =  !localUsername?.includes(userData?.external_id);
  const isImpersonation = useAuthStore(state => state.isImpersonation);

  const inititiveUrl =  smartConfiguration?.urlDomain
    ? `${smartConfiguration?.urlDomain}.${smartConfiguration.urlCorporateDomain}`
    : smartConfiguration?.urlCorporateDomain
      ?  `${smartConfiguration?.urlCorporateDomain}`
      : window.location.host;

  const initiatives = useMemo(
    () => userInfo?.initiatives?.flatMap((domain) => (
      (domain.id.toString() !== userData?.initiative_id?.toString()) ? [{
        icon: LucideCornerDownRight,
        id: domain.id,
        label: domain.label,
        link: "",
        type: "domain"
      }] : []
    )),
    [userData?.initiative_id, userInfo?.initiatives]
  );

  const isAdmin = userData?.permissions?.some(permission => (
    permission === PERMISSIONS["admin.dashboard.access"]
  ));
  const menuItems = getMenuItems();

  function deleteCookie(cname: string) {
    document.cookie =
    cname + "=delete;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/";
  }

  // cleanAllCookie è ora importata da utils/cookies come cleanAllCookies

  return (
    <StyledMenu
      //tabIndex={showDropdownMenu ? 0 : -1}
      id="menu-appbar"
      anchorEl={ anchorElDropdown }
      anchorOrigin={ {
        horizontal: 20,
        vertical: "bottom"
      } }
      classes={ { paper:"scrollable" } }
      className={ "profile-popOver" }
      transformOrigin={ {
        horizontal: "right",
        vertical: -10
      } }
      open={ showDropdownMenu }
      onClose={ hideMenu }
      sx={{
        ".MuiPaper-root": {
          top: !!showTutorialUserMenu ? "70px !important" : "0"
        },
        "& .MuiMenuItem-root.Mui-focusVisible": {
          outline: `4px solid ${theme.customColors.systemPrimary01}`
        },
        "&:focus": {
          outline: "none"
        }
      }}
    >
      {
        (isMobile && smartConfiguration?.chatbot) ? (
          <MenuItem
           //tabIndex={showDropdownMenu ? 0 : -1}
           //aria-label={ t("chatBot_menu_label") }
            onClick={ ()=> {
              showChatBot();
              hideMenu();
            } }
            sx={ {
              gap: 2,
              border: !!showTutorialUserMenu ? `1px solid ${theme.customColors.borderAccent}` : "0",
              borderRadius: !!showTutorialUserMenu ? "8px" : "0"
            } }
          >
            <ChatBotIcon
            aria-label={t("learning_coach")}>
              <img
                alt="chatBot"
                src={ chatBotIcon }
              />
            </ChatBotIcon>
            { t("chatBot_menu_label") }
          </MenuItem>
        ) : null
      }
      {
        initiatives?.map((initiative, index) => {
          const Icon = initiative.icon;

          return (
            <MenuItem
              //tabIndex={showDropdownMenu ? 0 : -1}
              //aria-label={ `${t("switch_to")} ${initiative.label}` }
              key={ index }
              sx={ {
                margin: theme.spacing(0, 2),
                padding: theme.spacing(2, 0),
                textTransform: "capitalize",

                ">a, p": {
                  alignItems: "center",
                  color: theme.customColors.textPrimary,
                  display: "flex",
                  fontWeight: 500,
                  gap: 2,
                  textAlign: "center",
                  textDecoration: "none"
                },
                "&.Mui-focusVisible": {
                  outline: `2px solid ${theme.palette.text?.primary}`,
                  outlineOffset: "2px",
                  borderRadius: "2px"
                }
              } }>
              <Typography
                tabIndex={0}
                aria-label={ `${t("switch_to")} ${initiative.label}` }
                textAlign="center"
                onClick={ (e) => {
                  e.preventDefault();
                  hideMenu();
                  if (
                    initiative.id
                    && session
                    && sessionToken
                    && userData?.organization_id
                  ) {

                    //Clean older cookies
                    document.cookie.split(";").forEach((cookie) => {
                      const cookieName = cookie.split("=")[0].trim();
                      if (cookieName.startsWith("tutorial-completed"))
                        document.cookie = `${cookieName}=;secure; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain=.${getLastPath()}; path=/`;
                    });

                    postInitiativeId(initiative.id);
                  }
                } }
              >
                <Icon
                  aria-hidden="true"
                  color={ theme.customColors.textPrimary }
                  size={ 24 }
                />
                { `${t("switch_to")} ${initiative.label}` }
              </Typography>
            </MenuItem>
          )
        })
      }
      {
        menuItems.map((menuItem, index) => {

          const Icon = menuItem.icon;

          return (
            <MenuItem
              //tabIndex={showDropdownMenu ? 0 : -1}
              //aria-label={ t(menuItem.label) }
              key={ index }
              sx={ {
                margin: theme.spacing(0, 2),
                padding: theme.spacing(2, 0),
                textTransform: "capitalize",

                ">a, p": {
                  alignItems: "center",
                  display: "flex",
                  gap: 2,

                  color: theme.customColors.textPrimary,
                  fontWeight: 500,
                  textAlign: "center",
                  textDecoration: "none"
                },

                "&:last-child": {
                  borderTop: `1px solid ${theme.customColors.border}`
                },
                "&.Mui-focusVisible": {
                  outline: `2px solid ${theme.palette.text?.primary}`,
                  outlineOffset: "2px",
                  borderRadius: "2px"
                }
              } }
            >
              {
                menuItem.label === "logout" ? (
                  <Typography
                    tabIndex={0}
                    aria-label={ t(menuItem.label) }
                    textAlign="center"
                    onClick={ (e) => {
                      e.preventDefault();
                      cleanAllCookies(["languageStored"]); // Usa la funzione centralizzata
                      hideMenu();
                      signOut();
                    } }

                  > <Icon aria-hidden="true" color={ theme.customColors.textPrimary } size={ 24 }/>
                    { t(menuItem.label) }
                  </Typography>
                ) : (menuItem.label === "language") ? (
                  <>
                    {language && (
                      <Typography
                        tabIndex={0}
                        aria-label={`${ language.label} (${language.id?.toUpperCase()})`}
                        textAlign="center"
                        onClick={ (e) => {
                          e.preventDefault();
                          hideMenu();
                          setLanguageModalOpen(true);
                        } }
                      >
                        <Icon aria-hidden="true" color={ theme.customColors.textPrimary } size={ 24 }/>
                        { `${ language.label} (${language.id?.toUpperCase()})` }
                      </Typography>
                    )}
                  </>
                ) : (
                  <Link
                    tabIndex={0}
                    href={ menuItem.link }
                    onClick={ () => {
                      if (menuItem.label === "switch_admin") {
                        deleteCookie("languageStored");
                      }
                    }}
                    target={ menuItem.target }
                    textAlign="center"
                    rel={ "noreferrer" }

                  > <Icon aria-hidden="true" color={ theme.customColors.textPrimary } size={ 24 }/>
                    { t(menuItem.label) }
                  </Link>
                )
              }
            </MenuItem>
          )
        })
      }
    </StyledMenu>
  );

  function getMenuItems() {
    const menuItems = [];

    const admin = {
      icon: LucideCornerDownRight,
      label: "switch_admin",
      link: `${adminLink}?sessionId=${sessionToken}&mode=${authenticationMode}&initiativeUrl=${inititiveUrl}`,
      // TODO impostando initiativeUrl quando passiamo ad admin se poi da admin impersonifichiamo un utente di
      //di una corporate diversa veniamo portati alla pagina portal con i dati corretti ma con l'url sbagliato
      //ovvero con l'url dell'iniziativa da cui abbiamo fatto switch e non con l'url 
      // dell'iniziativa/corporate dell'utente impersonificato
      target: "_self",
      type: "link"
    };

    const multiLanguage = {
      icon: LucideLanguages,
      label: "language",
      link: "",
      target: "_self",
      type: "link"
    };

    const logOut = {
      icon: LucideLogOut,
      label: "logout",
      link: "",
      target: "_self",
      type: "link"
    };

    if (isAdmin && !isImpersonation) { menuItems.push(admin) }
    if (isMultilanguage) { menuItems.push(multiLanguage)}
    menuItems.push(logOut);

    return menuItems;
  }
}

const HeaderAppBar = styled("nav")<{
  $mobileSearchOpen: boolean
  $showNotifications?: boolean
}>(({
  theme,
  $mobileSearchOpen,
  $showNotifications=false
}) => ({
  backgroundColor: theme.customColors?.backgroundPrimaryLighterAlternative,
  minHeight: "80px",
  padding: "12px 0",
  // pointerEvents: "none",
  position: "sticky",
  zIndex: $mobileSearchOpen ? "unset" : "1300",

  [theme.breakpoints.down("sm")]: {
    minHeight: "60px",
    padding: "10px 0",
    zIndex: !$showNotifications ? theme.zIndex.modal : $mobileSearchOpen ? "unset": theme.zIndex.snackbar,
    display: "flex",
    alignItems: "center"
  }
}));

function SupportAiButton() {
  const { openChat } = useAISupportChat();

  return (
    <ButtonText
      aria-label={ t("support_ai") }
      onClick={ openChat }
      sx={ {
        gap: "0.5rem",
        textTransform: "inherit",
        whiteSpace: "nowrap"
      } }
    >
      <LucideBot aria-hidden="true" size={ 20 } />
      { t("support_ai") }
    </ButtonText>
  );
}

function LanguageSelector({
  setOpenLanguageModal
} : {
  setOpenLanguageModal: Dispatch<SetStateAction<boolean>>
}) {
  const theme = useTheme();
  const language = useLanguageStore(state => state.language);

  return (
    <Stack
      alignItems="flex-end"
      zIndex={ theme.zIndex.fab }
    >
      {language && <Typography
        role="button"
        tabIndex={0}
        aria-label={t("language_select_header",{language:language.label})}
        textAlign="center"
        onClick={ () => {
          setOpenLanguageModal(true);
        } }
         onKeyDown={(e) => {
           if (e.key === "Enter" || e.key === " ") setOpenLanguageModal(true);
         }}
        sx={ {
          alignItems: "center",
          color: theme.customColors.headerIcon,
          cursor: "pointer",
          display: "flex",
          gap: 1,
          textAlign: "center",
          "&:focus": {
          outline: "none"
         },
          '&:focus-visible': {
          outline: `2px solid ${theme.customColors.headerIcon}`,
          outlineOffset: "2px"
         }
        } }
      >
        <LucideGlobe
          aria-hidden="true"
          color={ theme.customColors.headerIcon }
          size={ 24 }
        />
        { `${ language.label} (${language.id?.toUpperCase()})` }
      </Typography>}
    </Stack>
  );
}

const StyledSpan = styled("span")(({ theme }) => ({
  background: "transparent",

  position: "absolute",
  zIndex: theme.zIndex.mobileStepper,

  height: "100%",
  top: 0,
  width: "100%"
}));

function CloseDropdowns({
  closeDropdowns
} : {
  closeDropdowns: () => void
}) {
  return (
    <StyledSpan onClick={ closeDropdowns }></StyledSpan>
  );
}

function LinkLogo({ authenticated }: { authenticated: boolean }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {t} = useTranslation();
  const userMgmtData = useLoaderData() as { id: number, name: string, splash?: string};

  const setNavbarItem = useNavbarStore(state => state.setNavbarItem);
  const smartConfiguration = useAuthStore(state => state.smartConfiguration);

  const { digitedLogo, digidetLogoMobile, digitedLogoHeight, mobileLogoHeight } = getEnvironmentVariables();

  let logoHeight = "32px";
  if (isMobile){
    logoHeight = mobileLogoHeight ? mobileLogoHeight + "px" : "32px";
  }else{
    logoHeight = digitedLogoHeight ? digitedLogoHeight + "px" :
      smartConfiguration?.logoHeight ? smartConfiguration.logoHeight + "px" : "32px";
  }
  const headerLink = smartConfiguration?.visForyou === false ? "/esplora" : "/per-te";

  const pageTitle = getPageTitle(authenticated ? headerLink : "/login")?.toLowerCase() ?? "";

  return (
    <Link
      role="img"
      tabIndex={0}
      sx={{
         display: 'inline-block',
         lineHeight: 0,
        "&:focus": {
          outline: "none"
        },
        '&:focus-visible': {
         outline: `2px solid ${theme.customColors.headerIcon}`,
         outlineOffset: "2px"
        }
      }}
      aria-label={ `Logo ${userMgmtData.name}${pageTitle ? ` - ${t("back_to_page")} ${pageTitle}` : ""}` }
      href={ authenticated ? headerLink : "/logout" }
      onClick={ ()=> { authenticated && setNavbarItem("FOR_YOU") } }
      zIndex={ theme.zIndex.fab }
    >
      <Logo
        tabIndex={-1}
        alt={ `Logo ${userMgmtData.name}${pageTitle ? ` - ${t("back_to_page")} ${pageTitle}` : ""}` }
        width="auto"
        aria-hidden="true"
        height={ logoHeight }
        src={ isMobile ? digidetLogoMobile ?? logoMobileFallback : digitedLogo ?? logoFallback }
      />
    </Link>
  );
}
